import type { ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router';
import { HStack } from '@astryxdesign/core/HStack';
import { VStack } from '@astryxdesign/core/VStack';
import { Text } from '@astryxdesign/core/Text';
import type { IconProps } from '@phosphor-icons/react';

export interface BottomNavItem {
  to: string;
  label: string;
  icon: ComponentType<IconProps>;
  end?: boolean;
  /** Extra paths that belong to this tab (e.g. a booking's page under «Моя запись»). */
  match?: (pathname: string) => boolean;
}

const trim = (p: string) => (p.length > 1 ? p.replace(/\/$/, '') : p);

/** Floating tab bar above the home indicator; pages reserve space for it via `.app-page`. */
export function BottomNav({ items, label }: { items: BottomNavItem[]; label: string }) {
  const pathname = trim(useLocation().pathname);
  return (
    <HStack as="nav" aria-label={label} className="app-bottom-nav" gap={1} padding={1}>
      {items.map(({ to, label: text, icon: Icon, end, match }) => {
        const target = trim(to);
        const active = (end ? pathname === target : pathname === target || pathname.startsWith(`${target}/`)) || !!match?.(pathname);
        return (
          <NavLink key={to} to={to} end={end} className="app-tab" aria-label={text} data-active={active} aria-current={active ? 'page' : undefined}>
            <VStack align="center" gap={0.5} paddingBlock={1.5} paddingInline={2}>
              <Icon size={22} weight={active ? 'fill' : 'regular'} color={active ? 'var(--color-icon-accent)' : 'var(--color-text-secondary)'} aria-hidden />
              <Text size="2xs" weight={active ? 'semibold' : 'normal'} color={active ? 'primary' : 'secondary'}>
                {text}
              </Text>
            </VStack>
          </NavLink>
        );
      })}
    </HStack>
  );
}
